import React from 'react';
import Image from '@jetshop/ui/Image/Image';
import { styled } from 'linaria/react';
import { cx, css } from 'linaria';
import { theme } from '../Theme';
import { LinkEvaluatorExternal } from '../../utils/LinkEvaluatorExternal';
import HeroProductCard from '../Content/HeroProductCard';
import { htmlStyles } from './NewStartPageComponents/StartpageHtlm';

const ImageBoxWrapper = styled('div')`
  flex: 1;
  width: 100%;
  height: 100%;

  :hover {
    cursor: pointer;
    .light {
      background: ${theme.colors.hoverRed};
      color: #fff;
    }
    .dark {
      background: ${theme.colors.darkRed};
      color: #fff;
    }
  }

  [data-flight-image-children] {
    z-index: unset !important;
    position: absolute !important;
    top: 0;
    left: 0;
    display: flex;
    width: 100%;
    height: 100%;
  }
`;

const ContentWrapper = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 32px;
  color: ${(props) => props.color};

  p {
    font-size: 12px;
    line-height: 15px;
    font-weight: 400;
    text-align: center;
    padding: 3px;
  }

  &.TOP {
    align-self: flex-start;
  }
  &.MIDDLE {
    align-self: center;
  }
  &.BOTTOM {
    align-self: flex-end;
  }
  &.LEFT {
    align-items: flex-start;
    text-align: left;
  }
  &.CENTER {
    align-items: center;
    text-align: center;
  }
  &.RIGHT {
    align-items: flex-end;
    text-align: right;
  }
`;

export const CTA = styled('div')`
  text-decoration: none;
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 10px 0;
  font-size: 13px;
  line-height: 16px;
  letter-spacing: 0.1em;
  font-weight: ${(props) => props.fontweight ?? '700'};
  color: #fff;
  border-radius: 15px;
  padding: 0 2.5rem;
  width: fit-content;
  &.light {
    background: ${theme.colors.darkRed};
  }
  &.dark {
    background: ${theme.colors.hoverRed};
  }
  &.small {
    height: 24px;
    width: fit-content;
    padding: 0 1.3rem;
  }

  background: ${(props) => props.backgroundcolor}!important;
  color: ${(props) => props.textcolor}!important;
`;

export const textShadowStyle = css`
  &.shadow-none {
    text-shadow: none;
  }
  &.shadow-light {
    text-shadow: 0 0 10px rgba(255, 255, 255, 1);
  }
  &.shadow-dark {
    text-shadow: 0 0 10px rgba(0, 0, 0, 1);
  }
`;

const generateButtonColorsFromTheme = (buttonTheme) => {
  switch (buttonTheme) {
    case 'light':
      return {
        textcolor: theme.colors.white,
        backgroundcolor: theme.colors.darkRed,
      };
    case 'dark':
      return {
        textcolor: theme.colors.white,
        backgroundcolor: theme.colors.hoverRed,
      };
    default:
      return {
        color: '#000',
        backgroundcolor: '#fff',
      };
  }
};

//Imagebox
export const ImageBoxItem = ({
  imageUrl,
  title,
  smallTitle,
  html,
  contentVerticalAlignment,
  contentHorizontalAlignment,
  color,
  textShadow = { value: 'none' },
  link,
  buttonText,
  buttonTextFontWeight,
  buttonTheme,
  buttonTextColor,
  buttonBackgroundColor,
  buttonSize,
  position,
}) => {
  const vertAlign = contentVerticalAlignment;
  const horAlign = contentHorizontalAlignment;
  const sizes = ['1', '1', '1', '1/2', '1/2'];

  const buttonColors =
    buttonBackgroundColor?.value && buttonTextColor?.value
      ? {
          textcolor: buttonTextColor?.value,
          backgroundcolor: buttonBackgroundColor?.value,
        }
      : generateButtonColorsFromTheme(buttonTheme?.value);

  return (
    <ImageBoxWrapper
      className={cx('position' + position?.value, 'imagebox-wrapper')}
    >
      <LinkEvaluatorExternal link={link}>
        <Image
          className={cx(vertAlign?.value, horAlign?.value)}
          cover={true}
          src={imageUrl?.value}
          aspect={null}
          sizes={sizes}
          critical
        >
          <ContentWrapper
            className={cx(vertAlign?.value, horAlign?.value)}
            color={color?.value ?? 'transparent'}
          >
            {title?.value && (
              <h2
                className={cx(textShadowStyle, `shadow-${textShadow?.value}`)}
                dangerouslySetInnerHTML={{
                  __html: title?.value,
                }}
              ></h2>
            )}
            {smallTitle?.value && (
              <h3
                className={cx(textShadowStyle, `shadow-${textShadow?.value}`)}
                dangerouslySetInnerHTML={{
                  __html: smallTitle?.value,
                }}
              ></h3>
            )}
            {html?.value && (
              <div
                className={cx(
                  textShadowStyle,
                  `shadow-${textShadow?.value}`,
                  htmlStyles
                )}
                dangerouslySetInnerHTML={{
                  __html: html?.value,
                }}
              ></div>
            )}
            {buttonText?.value && (
              <CTA
                {...buttonColors}
                fontweight={buttonTextFontWeight?.value ?? '700'}
                className={cx(
                  buttonTheme?.value,
                  horAlign?.value,
                  buttonSize?.value
                )}
              >
                <span>{buttonText?.value}</span>
              </CTA>
            )}
          </ContentWrapper>
        </Image>
      </LinkEvaluatorExternal>
    </ImageBoxWrapper>
  );
};

const TextBoxWrapper = styled('div')`
  flex: 1;
  width: 100%;
  background: ${(props) => props.background};
  padding: ${(props) => props.padding};
  color: ${(props) => props.color};
  p {
    margin-bottom: 30px;
    font-size: 15px;
  }
  .content-wrapper {
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
  }

  a {
    text-decoration: none;
  }

  :hover {
    .CTA {
      cursor: pointer;
      .light {
        background: ${theme.colors.hoverRed};
        color: #fff;
      }
      .dark {
        background: ${theme.colors.darkRed};
        color: #fff;
      }
    }
  }
`;

//Textbox
export const TextBoxItem = ({
  title,
  smallTitle,
  html,
  textShadow = { value: 'none' },
  contentVerticalAlignment,
  contentHorizontalAlignment,
  color,
  link,
  buttonText,
  buttonTheme,
  buttonTextFontWeight = { value: '700' },
  buttonTextColor,
  buttonBackgroundColor,
  buttonSize,
  background,
  padding,
  position,
}) => {
  const vertAlign = contentVerticalAlignment;
  const horAlign = contentHorizontalAlignment;

  const buttonColors =
    buttonBackgroundColor?.value && buttonTextColor?.value
      ? {
          textcolor: buttonTextColor?.value,
          backgroundcolor: buttonBackgroundColor?.value,
        }
      : generateButtonColorsFromTheme(buttonTheme?.value);

  return (
    <TextBoxWrapper
      className={cx('position' + position?.value, 'textbox-wrapper')}
      background={background?.value ?? 'transparent'}
      color={color?.value ?? '#000'}
      padding={padding?.value ?? '0'}
    >
      <LinkEvaluatorExternal link={link}>
        <div className="content-wrapper">
          <ContentWrapper
            className={cx(vertAlign?.value, horAlign?.value)}
            color={color?.value ?? 'transparent'}
          >
            {title?.value && (
              <h2
                className={cx(textShadowStyle, `shadow-${textShadow?.value}`)}
                dangerouslySetInnerHTML={{
                  __html: title?.value,
                }}
              ></h2>
            )}
            {smallTitle?.value && (
              <h3
                className={cx(textShadowStyle, `shadow-${textShadow?.value}`)}
                dangerouslySetInnerHTML={{
                  __html: smallTitle?.value,
                }}
              ></h3>
            )}

            {html?.value && (
              <div
                className={cx(
                  textShadowStyle,
                  `shadow-${textShadow?.value}`,
                  htmlStyles
                )}
                dangerouslySetInnerHTML={{
                  __html: html?.value,
                }}
              ></div>
            )}
            {buttonText?.value && (
              <CTA
                {...buttonColors}
                fontweight={buttonTextFontWeight?.value ?? '700'}
                className={cx(
                  'CTA',
                  buttonTheme?.value,
                  horAlign?.value,
                  buttonSize?.value
                )}
              >
                <span>{buttonText?.value}</span>
              </CTA>
            )}
          </ContentWrapper>
        </div>
      </LinkEvaluatorExternal>
    </TextBoxWrapper>
  );
};

const ProductBoxWrapper = styled('div')`
  min-width: 30%;
  display: flex;
  flex-direction: column;
  div {
    a {
      height: 100%;
    }
  }
`;

//Productbox
export const ProductBoxItem = ({ product, position }) => {
  return (
    <ProductBoxWrapper
      className={cx('position' + position?.value, 'product-wrapper')}
    >
      <HeroProductCard product={product?.value} />
    </ProductBoxWrapper>
  );
};
